
import { useState, useCallback, Dispatch, SetStateAction } from 'react';
import { AppNode, ProcessDefinition, Edge, Swimlane } from '../types';
// Fix: Use named import for useReactFlow from reactflow.
import { useReactFlow } from 'reactflow';
import { useToast } from '../contexts/ToastContext';

// Vite's `import.meta.env.DEV` flag without pulling in vite/client types.
const IS_DEV =
    typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;

// Helper to safely deep clone nodes, handling potential circular refs gracefully if they exist
const safeDeepClone = <T>(obj: T): T => {
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (e) {
        console.error("Deep clone failed (likely circular reference). Falling back to sanitization.", e);
        if (Array.isArray(obj)) return [...obj] as unknown as T;
        return { ...(obj as object) } as T;
    }
};

// Internal ReactFlow-injected fields stripped before persisting — keeping them
// leads to stale layout/hit-test data on restore.
const INTERNAL_NODE_FIELDS = [
    'positionAbsolute', 'measured', 'handleBounds',
    'dragging', 'selected', 'resizing', 'width', 'height', 'z',
] as const;

const sanitizeNodes = (nodes: AppNode[]): AppNode[] => {
    // 1. First attempt a safe deep clone to break references
    const cleanNodes = safeDeepClone(nodes);

    return cleanNodes.map((node) => {
        // 2. Explicitly remove internal ReactFlow fields that cause issues on restore
        const mutable = node as unknown as Record<string, unknown>;
        INTERNAL_NODE_FIELDS.forEach(field => { delete mutable[field]; });

        // 3. Ensure position is valid
        if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
            console.warn(`Node ${node.id} has invalid position, resetting to {0,0}.`);
            node.position = { x: 0, y: 0 };
        }

        return node;
    });
};

const validateGraph = (nodes: AppNode[]): string[] => {
    if (IS_DEV) console.groupCollapsed("Graph Validation Trace");
    const errors: string[] = [];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // Check 1: Parent existence & Self-ref
    nodes.forEach(n => {
        if (n.parentNode) {
            if (n.parentNode === n.id) {
                const msg = `CRITICAL: Self-Reference detected. Node ${n.id} is its own parent.`;
                console.error(msg);
                errors.push(msg);
            }
            if (!nodeMap.has(n.parentNode) && IS_DEV) {
                console.warn(`Orphaned Node: ${n.id} references missing parent '${n.parentNode}'`);
            }
        }
    });

    // Check 2: Deep Cycle Detection
    // Trace up from every node to ensure no infinite loops in parent hierarchy
    nodes.forEach(startNode => {
        if (!startNode.parentNode) return;

        let currentId = startNode.parentNode;
        const path = [startNode.id];
        const visited = new Set<string>([startNode.id]);
        
        let depth = 0;
        const MAX_DEPTH = 500; // Safety brake

        while (currentId) {
            // Check if we hit a node we've already visited in this chain
            if (visited.has(currentId)) {
                 const cyclePath = [...path, currentId].join(' -> ');
                 const msg = `CRITICAL: Cycle Detected in parent hierarchy: ${cyclePath}`;
                 console.error(msg);
                 errors.push(msg);
                 break;
            }
            
            visited.add(currentId);
            path.push(currentId);
            
            const parent = nodeMap.get(currentId);
            if (!parent || !parent.parentNode) break; // Parent missing or root reached, stop tracing

            currentId = parent.parentNode;
            
            depth++;
            if (depth > MAX_DEPTH) {
                const msg = `Validation Aborted: Max depth exceeded for node ${startNode.id}. Chain: ${path.join(' -> ')}`;
                console.error(msg);
                errors.push(msg);
                break;
            }
        }
    });

    if (IS_DEV) console.groupEnd();
    return errors;
};

export const useProcessManagement = (
    nodes: AppNode[],
    setNodes: Dispatch<SetStateAction<AppNode[]>>,
    edges: Edge[],
    setEdges: Dispatch<SetStateAction<Edge[]>>,
    swimlanes: Swimlane[],
    setSwimlanes: Dispatch<SetStateAction<Swimlane[]>>,
    initialData?: ProcessDefinition[]
) => {
    const { fitView } = useReactFlow();
    const { addToast } = useToast();
    
    // Determine initial state
    const startingData = (initialData && initialData.length > 0) 
        ? initialData 
        : [{ id: 'proc_1', name: 'New Process', nodes: [], edges: [] } as ProcessDefinition];
    
    // Initialize with a Deep Copy to avoid mutating the source
    const [activeProcessId, setActiveProcessId] = useState<string>(startingData[0].id);
    const [processes, setProcesses] = useState<ProcessDefinition[]>(
        JSON.parse(JSON.stringify(startingData))
    );

    const handleSelectProcess = useCallback((id: string) => {
        if (id === activeProcessId) return;

        if (IS_DEV) console.group(`Switching Process: ${activeProcessId} -> ${id}`);
        try {
            // 1. Save Current State — snapshot swimlanes alongside nodes/edges
            //    so each process remembers its own lane layout.
            const currentNodesSanitized = sanitizeNodes(nodes);
            const currentSwimlanesSnapshot = safeDeepClone(swimlanes);

            setProcesses(prev => prev.map(p =>
                p.id === activeProcessId
                    ? { ...p, nodes: currentNodesSanitized, edges, swimlanes: currentSwimlanesSnapshot }
                    : p
            ));

            // 2. Find and Clone Target Process
            // We use a deep clone of the target process to ensure we are working on a 
            // fresh copy that we can mutate/fix without affecting the master state yet.
            const rawTargetProc = processes.find(p => p.id === id);
            
            if (!rawTargetProc) {
                console.error(`Process ${id} not found!`);
                addToast({ type: 'error', title: 'Error', message: 'Process not found.' });
                return;
            }

            // Create working copy
            const targetProc = safeDeepClone(rawTargetProc);

            // 3. Validate and Auto-Repair Target Graph
            const graphErrors = validateGraph(targetProc.nodes);
            
            if (graphErrors.length > 0) {
                if (IS_DEV) console.warn("Validation failed. Attempting auto-repair by stripping invalid parentNodes...");
                addToast({ 
                    type: 'warning', 
                    title: 'Graph Auto-Repaired', 
                    message: `Detected ${graphErrors.length} layout issues. Some groupings may be reset.` 
                });
                
                // Identify problematic node IDs from error messages
                targetProc.nodes = targetProc.nodes.map(n => {
                     // Check if this node is involved in any reported error
                     const isProblematic = graphErrors.some(e => e.includes(n.id));
                     
                     if (isProblematic && n.parentNode) {
                         if (IS_DEV) {
                             console.warn(`[Auto-Repair] Detaching node ${n.id} from parent ${n.parentNode} to break cycle.`);
                         }
                         const { parentNode: _parentNode, ...rest } = n;
                         // When detaching, reset position
                         return { ...rest, position: { x: 0, y: 0 } };
                     }
                     return n;
                });
            }

            // 4. Load Safe State
            const nodesToLoad = sanitizeNodes(targetProc.nodes);
            const edgesToLoad = targetProc.edges;
            const swimlanesToLoad: Swimlane[] = Array.isArray(targetProc.swimlanes)
                ? targetProc.swimlanes
                : [];

            setNodes(nodesToLoad);
            setEdges(edgesToLoad);
            setSwimlanes(swimlanesToLoad);
            setActiveProcessId(id);
            
            // 5. Reset View
            setTimeout(() => {
                fitView({ duration: 500 });
            }, 50);

        } catch (e) {
            const message = e instanceof Error ? e.message : 'Unknown error';
            console.error('Critical Failure Switching Process:', e);
            addToast({ type: 'error', title: 'System Error', message: 'Failed to load process: ' + message });
        } finally {
            if (IS_DEV) console.groupEnd();
        }
    }, [activeProcessId, nodes, edges, swimlanes, processes, setNodes, setEdges, setSwimlanes, fitView, addToast]);

    const handleCreateProcess = useCallback(() => {
        // Save current
        const currentNodesSanitized = sanitizeNodes(nodes);
        const currentSwimlanesSnapshot = safeDeepClone(swimlanes);
        setProcesses(prev => prev.map(p =>
            p.id === activeProcessId
                ? { ...p, nodes: currentNodesSanitized, edges, swimlanes: currentSwimlanesSnapshot }
                : p
        ));

        const newId = `proc_${Date.now()}`;
        const newProcess: ProcessDefinition = {
            id: newId,
            name: `Process ${processes.length + 1}`,
            nodes: [],
            edges: [],
            swimlanes: []
        };

        setProcesses(prev => [...prev, newProcess]);
        setNodes([]);
        setEdges([]);
        setSwimlanes([]);
        setActiveProcessId(newId);
        addToast({ type: 'success', title: 'Process Created', message: 'New blank process initialized.' });
    }, [activeProcessId, nodes, edges, swimlanes, processes, setNodes, setEdges, setSwimlanes, addToast]);

    const handleCreateSubProcess = useCallback(() => {
        // Save current
        const currentNodesSanitized = sanitizeNodes(nodes);
        const currentSwimlanesSnapshot = safeDeepClone(swimlanes);
        setProcesses(prev => prev.map(p =>
            p.id === activeProcessId
                ? { ...p, nodes: currentNodesSanitized, edges, swimlanes: currentSwimlanesSnapshot }
                : p
        ));

        const newId = `subproc_${Date.now()}`;
        const newProcess: ProcessDefinition = {
            id: newId,
            name: `Sub Process ${processes.length + 1}`,
            isSubProcess: true,
            nodes: [],
            edges: [],
            swimlanes: []
        };

        setProcesses(prev => [...prev, newProcess]);
        setNodes([]);
        setEdges([]);
        setSwimlanes([]);
        setActiveProcessId(newId);
        addToast({ type: 'success', title: 'Sub-Process Created', message: 'New blank sub-process initialized.' });
    }, [activeProcessId, nodes, edges, swimlanes, processes, setNodes, setEdges, setSwimlanes, addToast]);

    const handleDeleteProcess = useCallback((id: string) => {
        if (processes.length <= 1) {
            addToast({ type: 'warning', title: 'Action Prevented', message: "Cannot delete the last process." });
            return;
        }

        const isDeletingActive = id === activeProcessId;
        const newProcesses = processes.filter(p => p.id !== id);
        setProcesses(newProcesses);

        if (isDeletingActive) {
            const nextProc = newProcesses[0];
            const cleanNextNodes = sanitizeNodes(nextProc.nodes);
            const nextSwimlanes: Swimlane[] = Array.isArray(nextProc.swimlanes)
                ? safeDeepClone(nextProc.swimlanes)
                : [];

            setNodes(cleanNextNodes);
            setEdges(JSON.parse(JSON.stringify(nextProc.edges)));
            setSwimlanes(nextSwimlanes);
            setActiveProcessId(nextProc.id);
            setTimeout(() => fitView(), 50);
        }
        addToast({ type: 'info', title: 'Process Deleted', message: 'Process definition has been removed.' });
    }, [processes, activeProcessId, setNodes, setEdges, setSwimlanes, fitView, addToast]);

    const handleUpdateProcess = useCallback((id: string, data: Partial<ProcessDefinition>) => {
        setProcesses(prev => prev.map(p => 
            p.id === id ? { ...p, ...data } : p
        ));
    }, []);

    return {
        processes,
        setProcesses,
        activeProcessId,
        setActiveProcessId,
        handleSelectProcess,
        handleCreateProcess,
        handleCreateSubProcess,
        handleDeleteProcess,
        handleUpdateProcess
    };
};